import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import arrow_left from '../../assets/Main/arrow_left.svg'
import RegistrSelector from '../../components/lists/RegistrSelector'
import FileUpload from '../Registration/common/FileUpload'
import DatePicker from '../../pages/Registration/common/Calendar/DatePicker'
import apiClient from '../../api/client'
import {
  getCachedCities,
  getCachedPublicExecutors,
  getCachedServiceCatalog,
} from '../../api/frontendCache'
import {
  getCachedExecutor,
  getCachedFavoriteIds,
  invalidatePersonalAccountCache,
  updatePersonalAccountCache,
} from './personalAccountCache'
// import mockExecutors from '../../../src/mockExecutors.json'

import '../Main/Main.css'
import '../Customer/Customer.css'
import '../Customer/Customer.mobile.css'
import '../Customer/Customer.tablet.css'
import '../Executor/Executor.css'
import '../../styles/Modal.css'
import progress_bar from '../../assets/Main/progress_bar.svg'
import mob_progree_bar from '../../assets/Main/mob_progress_bar.svg'
import icon_close_modal from '../../assets/Main/icon_close_modal.svg'
import icon_arrow from '../../assets/Main/arrow_right_blue.svg'
import checkmark from '../../assets/Main/checkmark3.svg'
import icon_check from '../../assets/Main/icon_checkmark2.svg'
import icon_star_yellow from '../../assets/Main/icon_star_yellow.svg'
import award from '../../assets/Main/icon_award.svg'
import icon_star2 from '../../assets/Main/icon_star2.svg'
import icon_star_filled from '../../assets/Main/icon_star_filled.svg'
import avatarDefault from '../../assets/Main/avatar_vertical.svg'


const today = new Date()

const parseDate = (dateStr) => {
  if (!dateStr || dateStr.length !== 10) return null;
  
  const digits = dateStr.replace(/\D/g, '');
  if (digits.length !== 8) return null; 
  
  const day = parseInt(digits.slice(0, 2));
  const month = parseInt(digits.slice(2, 4));
  const year = parseInt(digits.slice(4, 8)); 
  
  return new Date(year, month - 1, day);
};

const isFutureDate = (dateStr) => {
  const parsed = parseDate(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed && !isNaN(parsed.getTime()) && parsed >= today;
};

const isValidDate = (dateStr) => {
  if (!dateStr || dateStr.length !== 10) return false;
  
  const parsed = parseDate(dateStr);
  return (
    parsed &&
    !isNaN(parsed.getTime()) &&
    parsed.getDate() === parseInt(dateStr.slice(0, 2)) &&
    parsed.getMonth() === parseInt(dateStr.slice(3, 5)) - 1 &&
    parsed.getFullYear() === parseInt(dateStr.slice(6, 10)) 
  );
};

const isDateOrderValid = (startStr, endStr) => {
  const start = parseDate(startStr)
  const end = parseDate(endStr)
  if (!start || !end) return true
  return start <= end
} 


export default function CreateOrderForm({ onClose, onCreate }) {
  const [step, setStep] = useState(1)
  const navigate = useNavigate()
  const [recommendedExecutors, setRecommendedExecutors] = useState([]);

  // Каталог услуг
  const [catalog, setCatalog] = useState([])
  const [loadingCatalog, setLoadingCatalog] = useState(true)
  const [catalogError, setCatalogError] = useState(null)

  // Услуги
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedService, setSelectedService] = useState(null)
  const [customServiceName, setCustomServiceName] = useState('')

  // Города
  const [citiess, setCitiess] = useState([])
  const [citiesWithMetro, setCitiesWithMetro] = useState([]) 
  const [selectedCityId, setSelectedCityId] = useState(null)

  // Форма
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [budget, setBudget] = useState('')
  const [requirements, setRequirements] = useState([])
  const [materials, setMaterials] = useState('')
  const [description, setDescription] = useState('')
  const [images, setImages] = useState([])
  const [isCheckedPolicy, setIsCheckedPolicy] = useState(false)
  
  // Новые чекбоксы
  const [asSoonAsPossible, setAsSoonAsPossible] = useState(false)
  const [budgetUndefined, setBudgetUndefined] = useState(false)

  const budgetRef = useRef(null)

  // Валидация
  const [startDateError, setStartDateError] = useState('')
  const [endDateError, setEndDateError] = useState('')
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isStepTwoValid, setIsStepTwoValid] = useState(false)

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)   

  // определение ширины устройства
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);  


  // Автофокус бюджета
  useEffect(() => {
    if (step === 2 && !budgetUndefined) {
      setTimeout(() => budgetRef.current?.focus(), 100)
    }
  }, [step, budgetUndefined])

  // Загрузка каталога
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        setLoadingCatalog(true)
        setCatalogError(null)
        const data = await getCachedServiceCatalog()
        setCatalog(data)
      } catch (err) {
        console.error('Ошибка каталога:', err)
        setCatalogError('Не удалось загрузить категории и услуги')
      } finally {
        setLoadingCatalog(false)
      }
    }
    fetchCatalog()
  }, [])

  // Загрузка городов
  useEffect(() => {
    const loadCities = async () => {
      try {
        const all = await getCachedCities()
        setCitiesWithMetro(all)
        setCitiess(all.map(c => ({ id: c.id.toString(), name: c.name })))
      } catch (err) {
        console.error('Ошибка городов:', err)
      }
    }
    loadCities()
  }, [])

  // Вычисляемые значения услуг
  const topCategories = useMemo(() => {
    if (!catalog || !Array.isArray(catalog)) return []
    return catalog.map(cat => ({
      id: cat.id,
      name: cat.name?.trim() || 'Без названия'
    }))
  }, [catalog])

  // Функция для получения исполнителей по специализациям
  const getRecommendedExecutors = async (categoryId, serviceId, limit = 3) => {
    try {
      const params = { 
        page: 0, 
        size: limit 
      };

      // Предпочтительно фильтруем по categoryId
      if (categoryId) {
        params.categoryIds = [categoryId];
      }
      // Если есть конкретная услуга — можно добавить и её
      else if (serviceId) {
        params.serviceIds = [serviceId];
      }

      return await getCachedPublicExecutors(params);
    } catch (err) {
      console.error('Ошибка загрузки рекомендованных исполнителей:', err);
      return [];
    }
  };

  // Запасной вариант — любые исполнители
  const getFallbackExecutors = async (limit = 3) => {
    try {
      const params = { page: 0, size: limit };
      return await getCachedPublicExecutors(params);
    } catch (err) {
      console.error('Ошибка загрузки любых исполнителей:', err);
      return [];
    }
  };


  // ===== ИЗБРАННОЕ =====
  const [favoriteStatus, setFavoriteStatus] = useState({});
  const [loadingFavorite, setLoadingFavorite] = useState({});

  // Добавьте функцию проверки избранного
  const checkFavorites = useCallback(async () => {
    const isAuthenticated = !!localStorage.getItem('accessToken');
    const userRole = localStorage.getItem('userRole');
    
    if (!isAuthenticated || userRole !== 'CUSTOMER' || recommendedExecutors.length === 0) {
      return;
    }

    try {
      const favIds = await getCachedFavoriteIds();
      
      const newStatus = {};
      recommendedExecutors.forEach((ex) => {
        newStatus[ex.id] = favIds.includes(ex.id);
      });

      setFavoriteStatus((prev) => ({ ...prev, ...newStatus }));
    } catch (err) {
      if (err.response?.status === 403) {
        console.log('Пользователь не является заказчиком, избранное недоступно');
      } else if (err.response?.status !== 401) {
        console.error('Ошибка при загрузке избранного:', err);
      }
    }
  }, [recommendedExecutors]);

  // Переключение избранного
  const toggleFavorite = async (executorId) => {
    const isAuthenticated = !!localStorage.getItem('accessToken');
    const userRole = localStorage.getItem('userRole');

    if (!isAuthenticated) {
      alert('Авторизуйтесь на платформе, чтобы продолжить');
      return;
    }

    if (userRole !== 'CUSTOMER') {
      alert('Только заказчики могут добавлять исполнителей в избранное');
      return;
    }

    const isCurrentlyFavorite = favoriteStatus[executorId] || false;
    const method = isCurrentlyFavorite ? 'delete' : 'post';

    setLoadingFavorite(prev => ({ ...prev, [executorId]: true }));

    try {
      const response = await apiClient[method](`/customers/me/relations/executors/${executorId}/favorites`);

      if (response.status === 204 || response.status === 200) {
        updatePersonalAccountCache('customer-favorite-ids', (ids) =>
          isCurrentlyFavorite
            ? ids.filter((id) => id !== executorId)
            : [...new Set([...ids, executorId])]
        );
        setFavoriteStatus(prev => ({
          ...prev,
          [executorId]: !isCurrentlyFavorite,
        }));
      }
    } catch (err) {
      console.error('❌ Ошибка при работе с избранным:', err);

      if (err.response?.status === 403) {
        alert('Только заказчики могут добавлять в избранное');
      } else if (err.response?.status === 404) {
        alert('Исполнитель не найден');
      } else if (err.response?.status !== 401) {
        alert('Не удалось обновить избранное');
      }
    } finally {
      setLoadingFavorite(prev => ({ ...prev, [executorId]: false }));
    }
  };

  useEffect(() => {
    if (recommendedExecutors.length > 0) {
      checkFavorites();
    }
  }, [recommendedExecutors, checkFavorites]);



  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Неизвестно';
    const { unit, value } = lastSeen;
    if (unit === 'LESS_THAN_MINUTE') return 'меньше минуты назад';
    const map = { MINUTE: 'мин', MINUTES: 'мин', HOUR: 'ч', HOURS: 'ч', DAY: 'д', DAYS: 'д' };
    return `${value} ${map[unit] || unit?.toLowerCase() || ''} назад`;
  };

  const filteredServices = useMemo(() => {
    if (!selectedCategory || !catalog) return []

    const rootCat = catalog.find(cat => cat.id === selectedCategory.id)
    if (!rootCat) return []

    const allServices = []

    const collectServices = (node) => {
      if (Array.isArray(node.services)) {
        node.services.forEach(s => {
          if (s?.name?.trim()) {
            allServices.push({ id: s.id, name: s.name.trim() })
          }
        })
      }
      if (Array.isArray(node.children)) {
        node.children.forEach(collectServices)
      }
    }

    collectServices(rootCat)

    return allServices
  }, [selectedCategory, catalog])

  // Валидация шага 1
  const isStepOneValid = useMemo(() => {
    const datesValid = asSoonAsPossible ? true : (
      startDate.replace(/\D/g, '').length === 8 &&
      endDate.replace(/\D/g, '').length === 8 &&
      !startDateError &&
      !endDateError &&
      parseDate(startDate) <= parseDate(endDate) &&
      isFutureDate(startDate) &&
      isFutureDate(endDate)
    )

    const titleValid = selectedService
      ? (selectedService.name !== 'Другое' || customServiceName.trim().length >= 3)
      : false

    return (
      !!selectedCategory &&
      !!selectedService &&
      !!selectedCityId &&
      datesValid &&
      titleValid
    )
  }, [
    selectedCategory,
    selectedService,
    selectedCityId,
    startDate,
    endDate,
    startDateError,
    endDateError,
    customServiceName,
    asSoonAsPossible
  ])

  // Валидация шага 2
  useEffect(() => {
    const budgetValid = budgetUndefined ? true : (Number(budget) >= 500)
    const requirementsValid = requirements.length > 0
    const materialsValid = !!materials
    const descriptionValid = description.trim().length >= 3
    setIsStepTwoValid(budgetValid && requirementsValid && materialsValid && descriptionValid)
  }, [budget, requirements, materials, description, budgetUndefined])

  const handleDateChange = (value, type) => {
    const digits = value.replace(/\D/g, '').slice(0, 8); 
    let formatted = digits;
    
    if (digits.length > 4) {
      formatted = digits.slice(0, 2) + '.' + digits.slice(2, 4) + '.' + digits.slice(4, 8);
    } else if (digits.length > 2) {
      formatted = digits.slice(0, 2) + '.' + digits.slice(2);
    }

    if (type === 'start') {
      setStartDate(formatted);
      if (digits.length === 8) { 
        if (!isValidDate(formatted)) setStartDateError('Некорректная дата');
        else if (!isFutureDate(formatted)) setStartDateError('Дата не может быть в прошлом');
        else setStartDateError('');
      } else setStartDateError('');
    } else {
      setEndDate(formatted);
      if (digits.length === 8) { 
        if (!isValidDate(formatted)) setEndDateError('Некорректная дата');
        else if (!isFutureDate(formatted)) setEndDateError('Дата не может быть в прошлом');
        else setEndDateError('');
      } else setEndDateError('');
    }
  };

  const toggleRequirement = (value) => {
    setRequirements(prev => prev.includes(value) ? prev.filter(r => r !== value) : [...prev, value])
  }

  // Создание заказа
  const handleCreate = async () => {
    if (isLoading) return

    if (!selectedService?.id) {
      alert("Выберите услугу")
      return
    }
    if (!selectedCityId) {
      alert("Выберите город")
      return
    }

    setIsLoading(true)

    try {
      // Проверка файлов
      const MAX_FILES = 3
      const MAX_SIZE_MB = 10
      if (images.length > MAX_FILES) {
        alert(`Максимум ${MAX_FILES} файлов`)
        return
      }
      for (const file of images) {
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
          alert(`Файл «${file.name}» слишком большой`)
          return
        }
      }

      // Даты и длительность
      let durationValue, durationUnit
      const start = parseDate(startDate)
      const end = parseDate(endDate)
      
      if (asSoonAsPossible) {
        durationValue = null
        durationUnit = null
      } else {
        if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
          alert("Укажите корректные даты")
          return
        }
        durationValue = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)))
        durationUnit = "DAYS"
      }

      // Материалы
      let materialsProvider = "BUY_MYSELF"
      if (materials === "Уже куплены") materialsProvider = "ALREADY_BOUGHT"
      if (materials === "От исполнителя") materialsProvider = "FROM_EXECUTOR"

      const isOther = selectedService?.name === "Другое"

      // Находим выбранный город
      const selectedCity = citiess.find(c => c.id === selectedCityId)

      // Формируем payload
      const payload = {
        serviceIds: [selectedService.id],
        description: description.trim() || "",
        budgetRub: budgetUndefined ? null : (budget ? Number(budget) : 0),
        ...(durationValue && { durationValue }),
        ...(durationUnit && { durationUnit }),
        requireContract: requirements.includes("Работа по договору"),
        requireLicense: requirements.includes("Наличие лицензии"),
        onlyLegalEntity: requirements.includes("Юридическое лицо"),
        hasWarranty: requirements.includes("Гарантия на работу"),
        materialsProvider,
        location: {
          regionType: "CITY",
          cityId: selectedCityId,
          regionId: selectedCity?.regionId || citiesWithMetro.find(c => c.id.toString() === selectedCityId)?.regionId || "",
          address: ""
        }
      }

      // Добавляем title ТОЛЬКО если это "Другое"
      if (isOther) {
        payload.title = customServiceName.trim()
      }

      const createRes = await apiClient.post('/customers/me/orders', payload)
      const orderId = createRes.data?.id
      if (!orderId) throw new Error("Нет ID заказа")

      // Загрузка файлов
      let uploadedAttachments = []
      if (images.length > 0) {
        const fd = new FormData()
        images.forEach((file) => {
          fd.append("attachments", file)
        })

        try {
          const attachRes = await apiClient.post(
            `/customers/me/orders/${orderId}/attachments`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } }
          )
          uploadedAttachments = attachRes.data?.attachments || []
        } catch (attachErr) {
          console.error('Ошибка загрузки файлов:', attachErr)
          alert('Не удалось загрузить прикрепленные файлы')
        }
      }


      const createdOrder = {
        ...createRes.data,
        attachments: uploadedAttachments
      }

      let executors = [];
      const NEEDED_COUNT = 2;

      // 1. Пробуем по категории (самый логичный вариант)
      if (selectedCategory?.id) {
        executors = await getRecommendedExecutors(selectedCategory.id, null, NEEDED_COUNT);
      }

      // 2. Если пусто — пробуем по конкретной услуге
      if (executors.length < NEEDED_COUNT && selectedService?.id) {
        const moreExecutors = await getRecommendedExecutors(
          null, 
          selectedService.id, 
          NEEDED_COUNT - executors.length
        );
        executors = [...executors, ...moreExecutors];
      }

      // 3. Если всё ещё пусто — берём любых
      if (executors.length < NEEDED_COUNT) {
        const moreExecutors = await getFallbackExecutors(NEEDED_COUNT - executors.length);
        executors = [...executors, ...moreExecutors];
      }

      // Дальше — загрузка деталей
      const uniqueExecutors = Array.from(
        new Map(executors.map(ex => [ex.id, ex])).values()
      ).slice(0, NEEDED_COUNT);

      const executorsWithDetails = await Promise.all(
        uniqueExecutors.map(async (ex) => {
          try {
            const details = await getCachedExecutor(ex.id);
            return { ...ex, ...details, avatar: ex.avatarUrl || avatarDefault };
          } catch {
            return ex;
          }
        })
      );

      setRecommendedExecutors(executorsWithDetails);

      // 1. Сначала сохраняем данные в состояние (рекомендации + заказ)
      // 2. Показываем модалку успеха
      setShowSuccessModal(true);

      invalidatePersonalAccountCache('customer-orders');

      // 3. Только после этого уведомляем родителя (и обычно форма закроется)
      onCreate(createdOrder);
      
   
    } catch (err) {
      console.error("Ошибка:", err)
      let msg = "Не удалось создать заказ"
      if (err.response) {
        const { status, data } = err.response
        console.log("Ответ сервера:", data)
        msg = data?.message || `Ошибка ${status}`
      }
      alert(msg)
    } finally {
      setIsLoading(false)
    }
  };


  return (
    <div className='page-wrapper'>
      <title>Создание заказа | Rulla</title>

      <div className="create-order-form">
        <button onClick={onClose} className="back-btn">
          <img src={arrow_left} alt="Назад" />
        </button>

        <h2 >Найти специалиста для ремонта</h2>

        {step === 2 && (
          <div>
            <img src={isMobile?mob_progree_bar:progress_bar} alt='Шкала прогресса' style={{ margin:'-30px 0 7px 0', width: '100%' }} />
            <p style={{ textAlign: 'center', fontSize:isMobile?'16px':'20px', color: '#656565', lineHeight: '1.2', marginBottom: '0' }}>
              Чем точнее вы укажете детали заказа, тем лучше подрядчики смогут оценить работу и предложить наиболее выгодные условия
            </p>
          </div>
        )}

        {step === 1 && (
          <>
            <div className="passport-field">
              <h3 style={{fontSize: isMobile ? '18px' : ''}}>Категория</h3>
              <div className="registr-selector">
                <RegistrSelector
                  placeholder="Выберите категорию"
                  subject={topCategories.map(c => c.name)}
                  onSelect={name => {
                    const cat = topCategories.find(c => c.name === name)
                    setSelectedCategory(cat)
                    setSelectedService(null)
                  }}
                  selected={selectedCategory?.name || ''}
                />
              </div>
            </div>

            <div className="passport-field">
              <h3 style={{fontSize: isMobile ? '18px' : ''}}>Что нужно сделать?</h3>
              <div className="registr-selector">
                <RegistrSelector
                  placeholder="Выберите услугу"
                  subject={filteredServices.map(s => s.name)}
                  onSelect={name => {
                    const serv = filteredServices.find(s => s.name === name)
                    setSelectedService(serv)
                    setCustomServiceName('')
                  }}
                  selected={selectedService?.name || ''}
                  disabled={!selectedCategory || loadingCatalog}
                />
              </div>
              {selectedService?.name === 'Другое' && (
                <div style={{ marginTop: '12px' }}>
                  <input
                    type="text"
                    className="description-input"
                    placeholder="Введите собственную услугу"
                    value={customServiceName}
                    onChange={e => setCustomServiceName(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Только выбор города */}
            <div className="passport-field">
              <h3 style={{fontSize: isMobile ? '18px' : ''}}>Местоположение</h3>
              <div className="registr-selector">
                <RegistrSelector
                  placeholder="Выберите город"
                  subject={citiess.map(c => c.name)}
                  onSelect={name => {
                    const city = citiess.find(c => c.name === name)
                    if (city) {
                      setSelectedCityId(city.id.toString())
                    }
                  }}
                  selected={citiess.find(c => c.id.toString() === selectedCityId)?.name || ''}
                />
              </div>
            </div>

            <div className="passport-field">
              <h3 style={{fontSize: isMobile ? '18px' : ''}}>
                Сроки
                {(!asSoonAsPossible && (startDateError || endDateError || !isDateOrderValid(startDate, endDate))) && (
                  <span style={{ color: '#ff4444', marginLeft: '10px', fontSize: isMobile?'14px':'16px' }}>
                    {startDateError || endDateError || 'Дата окончания раньше начала'}
                  </span>
                )}
              </h3>
              <div className="dates-row">
                <DatePicker
                  value={startDate}
                  onChange={v => handleDateChange(v, 'start')}
                  placeholder="Начало"
                  error={!!startDateError && !asSoonAsPossible}
                  disabled={asSoonAsPossible}
                />
                <DatePicker
                  value={endDate}
                  onChange={v => handleDateChange(v, 'end')}
                  placeholder="Конец"
                  error={!!endDateError && !asSoonAsPossible}
                  disabled={asSoonAsPossible}
                />
              </div>

              {/* Чекбокс вместо радио */}
              <div className="checkbox-wrapper" onClick={() => setAsSoonAsPossible(!asSoonAsPossible)} style={{ marginTop: '10px' }}>
                <div className={`custom-checkbox ${asSoonAsPossible ? 'checked' : ''}`}>
                  {asSoonAsPossible && (
                    <svg width="14" height="10" viewBox="0 0 14 10" fill="none" className="check-icon">
                      <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <h3 style={{marginTop: '10px', fontSize: '18px'}}>Как можно скорее</h3>
              </div>
            </div>

            <button
              className={`continue-button ${!isStepOneValid ? 'disabled' : ''}`}
              disabled={!isStepOneValid}
              onClick={() => setStep(2)}
              style={{ margin: isMobile?'0px 0 0':'20px 0 0 0' }}
            >
              Продолжить
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div className="passport-field" style={{ position: 'relative' }}>
              <h3 style={{fontSize: isMobile ? '18px' : ''}}>Бюджет проекта</h3>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  ref={budgetRef}
                  value={budget}
                  onChange={e => setBudget(e.target.value.replace(/\D/g, ''))}
                  style={{ width: '100%', paddingRight: '40px' }}
                  placeholder='до'
                  disabled={budgetUndefined}
                />
                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#656565', fontSize: '20px', pointerEvents: 'none' }}>₽</span>
              </div>
              
              {/* Чекбокс вместо радио */}
              <div className="checkbox-wrapper" onClick={() => setBudgetUndefined(!budgetUndefined)} style={{ margin: '10px 0 0 0' }}>
                <div className={`custom-checkbox ${budgetUndefined ? 'checked' : ''}`}>
                  {budgetUndefined && (
                    <svg width="14" height="10" viewBox="0 0 14 10" fill="none" className="check-icon">
                      <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <span style={{ fontSize: isMobile?'14px':'18px', color: '#000', fontWeight: '500', lineHeight: '1.2' }}>Не определен, готов рассматривать предложения специалистов</span>
              </div>
            </div>

            <h3 style={{ fontSize:isMobile?'18px':'20px', fontWeight: '500', color: '#000', marginBottom: '-30px' }}>Требования к исполнителю</h3>
            <div className='checkbox-grid' >
              {['Работа по договору', 'Наличие лицензии', 'Гарантия на работу', 'Юридическое лицо'].map(r => (
                <div key={r} className="checkbox-wrapper" onClick={() => toggleRequirement(r)} style={{marginBottom: '-10px'}}>
                  <div className={`custom-checkbox ${requirements.includes(r) ? 'checked' : ''}`}>
                    {requirements.includes(r) && (
                      <svg width="14" height="10" viewBox="0 0 14 10" fill="none" className="check-icon">
                        <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span className='checkbox-text' style={{color: '#000', fontWeight: '500', fontSize:isMobile?'16px':'20px'}}>{r}</span>
                </div>
              ))}
            </div>

            <h3 style={{ fontSize:isMobile?'18px':'20px', fontWeight: '500', color: '#000', margin: '10px 0 -15px 0' }}>Материалы</h3>
            {['Уже куплены', 'Закуплю сам', 'От исполнителя'].map(m => (
              <label key={m} className="radio-option" style={isMobile?{marginTop:0}:{}}>
                <input type="radio" checked={materials === m} onChange={() => setMaterials(m)} />
                {m}
              </label>
            ))}

            <div className='passport-field' style={{ marginTop: '15px' }}>
              <h3 style={{ fontSize:isMobile?'18px':''}}>Описание</h3>
              <textarea
                placeholder='Добавьте описание своего проекта'
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="description-input"
                style={isMobile?{borderRadius:'7px'}:{}}
              />
            </div>

            <div className='passport-field'>
              <h3 style={{ marginBottom: '-5px', fontSize:isMobile?'18px':'' }}>Желаемый результат</h3>
              <p style={{ width: '70%', marginBottom: '10px' }}>Добавьте до 3 фото желаемого результата</p>
              <FileUpload maxFiles={5} onFilesUpload={setImages} />
            </div>

            <button
              className={`continue-button ${!isStepTwoValid || isLoading ? 'disabled' : ''}`}
              disabled={!isStepTwoValid || isLoading}
              onClick={handleCreate}
              style={{ marginBottom: '0' }}
            >
              {isLoading ? 'Сохраняем...' : 'Разместить заказ'}
            </button>
          </>
        )}
      </div>



      {/* Модалка успеха */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={onClose} style={{ overflowY: 'auto', margin: 0 }}>
          <div className="response-sent-modal-window" onClick={e => e.stopPropagation()} style={{ margin:isMobile?'900px 0 100px 0':'25% 0 100px 0', overflowY: 'auto', padding:isMobile?'20px':'' }}>
            <button className="response-sent-modal-close" onClick={onClose} style={{top: isMobile?'10px':'', right: isMobile?'10px':''}}>
              <img src={icon_close_modal} alt="Закрыть" />
            </button>

            <div className="response-sent-modal-plane">
              <img src={checkmark} alt='Галочка' />
            </div>

            <h2 className="response-sent-modal-title" style={{ margin: '30px 0 10px 0' }}>Ваш заказ опубликован!</h2>
            <p style={{ textAlign: 'center', color: '#656565', fontSize: isMobile?'16px':'20px', fontWeight: '500', margin: '0 0 20px 0', lineHeight: '1.3' }}>
              Отклики специалистов вы можете <br /> видеть в личном кабинете
            </p>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0' }}>
              <button className="response-sent-modal-chat-btn" onClick={() => {
                setShowSuccessModal(false)
                navigate('/customer_personal_account')
              }}>
                Перейти в личный кабинет
              </button>
            </div>

            <div className="response-sent-similar-title" style={{ fontWeight: '600', margin: '-20px 0 0 0' }}>
              <span style={{marginBottom: isMobile?'-10px':''}}>Вам могут подойти:</span>
              <Link to={'/customer_all_specialists'} className="detail-link" style={{ margin: '0 0 5px 0' }}>
                Все специалисты
                <img src={icon_arrow} alt="Стрелка" style={{ margin: '-5px 0 0 15px' }} />
              </Link>
            </div>

            <div className="response-sent-similar-grid">
              {recommendedExecutors.length > 0 ? (
                recommendedExecutors.slice(0, 2).map(executor => (
                  <div key={executor.id} className="task-card-modern" style={{ border: '1px solid #919191', padding:isMobile?'15px':'25px', borderRadius: '15px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                      <div style={{ width: '140px', height: '190px', borderRadius: '7px', marginBottom: '15px', overflow: 'hidden', background: '#e0e0e0' }}>
                        <img
                          src={executor.avatarUrl || 'https://via.placeholder.com/300x160?text=Фото+не+загружено'}
                          alt={executor.displayName || executor.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={e => { e.target.src = 'https://via.placeholder.com/300x160?text=Фото+не+загружено' }}
                        />
                      </div>
                      <div className="name-block" style={{ marginLeft: '20px' }}>
                        {!isMobile && (
                        <div className="verified">
                          <img src={icon_check} alt="✓" width={23} />
                          <span style={{ fontSize: '14px' }}> Документы подтверждены </span>
                        </div> )}

                        <h3 className="executor-name" style={{ fontSize: '20px' }}>{executor.displayName || executor.name}</h3>
                        <p className="last-online">Был в сети {formatLastSeen(executor.lastSeenAgo) || 'недавно'}</p>
                        <div className="rating">
                          <span>{executor.rating ? Number(executor.rating).toFixed(1) : '—'}</span>
                          <img src={icon_star_yellow} alt="★" className="star-icon" />
                          <span>{executor.reviewsCount || 0} отзывов</span>
                        </div>
                        {executor.awards && executor.awards.length > 0 && (
                          <div className="award">
                            <img src={award} alt="Награда" />
                            <span>{executor.awards[0].name || 'Награда'}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {isMobile && (
                        <div className="verified" style={{marginBottom: '20px'}}>
                          <img src={icon_check} alt="✓" width={23} />
                          <span style={{ fontSize: '14px' }}> Документы подтверждены </span>
                    </div> )}

                    {/* <div className="actions">
                      <button className="offer-btn" style={{ height: '70px' }}>Предложить заказ</button>
                      <button 
                        className="favorite-btn"
                        onClick={() => toggleFavorite(executor.id)}
                        disabled={loadingFavorite[executor.id]}
                        style={{
                          opacity: loadingFavorite[executor.id] ? 0.6 : 1,
                          cursor: loadingFavorite[executor.id] ? 'wait' : 'pointer',
                        }}
                      >
                        <img 
                          src={favoriteStatus[executor.id] ? icon_star_filled : icon_star2} 
                          alt="★" 
                          width={30} 
                        />
                      </button>
                    </div> */}

                    <div className="features" style={{ flexDirection: 'column', gap: '10px', margin: isMobile?'40px 0':'15px 0', gap: isMobile?'30px':'' }}>
                      {executor.readyToContract && <span><img src={icon_check} alt="✓" style={{ marginRight: '7px' }} /> Работа по договору</span>}
                      {executor.readyToGiveWarranty && <span><img src={icon_check} alt="✓" style={{ marginRight: '7px' }} /> Гарантия на работу</span>}
                    </div>

                    {executor.specialOffer && executor.specialOffer.discountPercent && (
                      <div className="special-offer" style={{ marginBottom:isMobile?'40px':'30px', marginTop: isMobile?'-10px':'20px' }}>
                        <div className="discount">-{executor.specialOffer.discountPercent}%</div>
                        <h4>Спецпредложение</h4>
                        <p>{executor.specialOffer.text}</p>
                      </div>
                    )}

                    <Link to={`/executor_profile/${executor.id}`} className="btn-look-more">
                      Посмотреть еще
                      <img src={icon_arrow} alt="Стрелка" style={{ margin: '-5px 0 0 15px' }} />
                    </Link>
                  </div>
                ))
              ) : (
                // Если нет исполнителей, показываем заглушку или ничего
                <p style={{ textAlign: 'center', color: '#656565' }}>Скоро здесь появятся рекомендации</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
